import { Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import { CatalogTableRow } from '@backstage/plugin-catalog';
import { TableColumn } from '@backstage/core-components';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { RiRocketLine } from '@remixicon/react';
import {
  BUILD_STATUS_ANNOTATION,
  IMAGE_FAMILY_ANNOTATION,
  SOURCE_REPO_ANNOTATION,
  imageLaunchUrl,
} from './catalogInfraTypes';
import { createCreatedByColumn } from './CustomCatalogPage';
import { InfraStatusChip } from './InfraStatusChip';
import styles from './imagesCatalog.module.css';

const IMAGE_VERSION_ANNOTATION = 'infrastructure.backstage.io/image-version';

function imageDisplayLabel(entity: Entity): string {
  const repo = entity.metadata.annotations?.[SOURCE_REPO_ANNOTATION] ?? '';
  const version = entity.metadata.annotations?.[IMAGE_VERSION_ANNOTATION];
  const repoShort =
    repo.replace('https://github.com/', '').split('/').pop() ??
    entity.metadata.title ??
    entity.metadata.name;
  return version ? `${repoShort} · ${version}` : repoShort;
}

function shortImageFamily(entity: Entity): string {
  const family = entity.metadata.annotations?.[IMAGE_FAMILY_ANNOTATION];
  if (!family) {
    return (
      entity.metadata.annotations?.[IMAGE_VERSION_ANNOTATION] ??
      '-'
    );
  }
  return family.split('/').pop() ?? family;
}

const createImageNameColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Image',
  field: 'entity.metadata.name',
  width: '18%',
  render: (row: CatalogTableRow) => {
    const entity = row.entity as Entity;
    return (
      <EntityRefLink entityRef={entity} defaultKind="Component">
        {imageDisplayLabel(entity)}
      </EntityRefLink>
    );
  },
});

const createImageFamilyColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Family',
  field: 'entity.metadata.annotations',
  width: '22%',
  render: (row: CatalogTableRow) => {
    const entity = row.entity as Entity;
    const family = shortImageFamily(entity);
    return <span className={styles.familyText}>{family}</span>;
  },
});

const createBuildStatusColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Status',
  field: 'entity.metadata.annotations',
  width: '10%',
  render: (row: CatalogTableRow) => {
    const entity = row.entity as Entity;
    const buildStatus =
      entity.metadata.annotations?.[BUILD_STATUS_ANNOTATION] ?? '';
    return <InfraStatusChip status={buildStatus} />;
  },
});

const createSourceRepoColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Source repo',
  field: 'entity.metadata.annotations',
  width: '18%',
  render: (row: CatalogTableRow) => {
    const entity = row.entity as Entity;
    const repo = entity.metadata.annotations?.[SOURCE_REPO_ANNOTATION];
    if (!repo) {
      return '-';
    }
    const short = repo.replace('https://github.com/', '');
    return <span className={styles.repoText}>{short}</span>;
  },
});

const createLaunchVmColumn = (): TableColumn<CatalogTableRow> => ({
  title: '',
  field: 'id',
  width: '10%',
  render: (row: CatalogTableRow) => {
    const entity = row.entity as Entity;
    const buildStatus =
      entity.metadata.annotations?.[BUILD_STATUS_ANNOTATION] ?? '';
    if (buildStatus !== 'success') {
      return null;
    }
    const entityRef = stringifyEntityRef(entity);
    return (
      <div className={styles.launchCell}>
        <Button
          component={Link}
          to={imageLaunchUrl(entityRef)}
          variant="outlined"
          color="primary"
          size="small"
          className={styles.launchButton}
          startIcon={<RiRocketLine size={16} />}
        >
          Launch
        </Button>
      </div>
    );
  },
});

export const imageCatalogColumns = [
  createImageNameColumn(),
  createSourceRepoColumn(),
  createImageFamilyColumn(),
  createBuildStatusColumn(),
  createCreatedByColumn(),
  createLaunchVmColumn(),
];
